import { motion } from "framer-motion";
import { SpotifyEmbed, SoundCloudEmbed } from "@/components/EmbedWrappers";
import SEO from "@/components/SEO";
import { useCMS } from "@/hooks/useCMS";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: "easeOut" } }),
};

/* Hardcoded fallback */
const defaultDiscography = [
  { title: "Want That", label: "Single", year: "2026", artwork_url: "/api/files/phoon-site/uploads/e00eb83b-e4c3-4a2e-8cb6-f8a8a5ea2fe8.PNG", spotify_url: "https://open.spotify.com/album/1TFEMiN4M2pLNwyg9eZAFG" },
  { title: "She Said", label: "Single", year: "2026", artwork_url: "/api/files/phoon-site/uploads/5176d079-0a39-4a4a-b809-99c86c140ab3.jpeg", spotify_url: "https://open.spotify.com/album/5KZNb9Xh3BTAGZNgYvBrNn" },
  { title: "Bad Lil Thing", label: "Control & Freak", year: "2026", artwork_url: "/api/files/phoon-site/uploads/8eb56cee-5073-424a-98ee-58539ff807d1.JPG", spotify_url: "https://open.spotify.com/album/5ghfGA7IBVsNReZr8aXjIy" },
  { title: "Bounce It", label: "Single", year: "2025", artwork_url: "/api/files/phoon-site/uploads/2adc8d7f-5a4f-47a6-9cce-acf9f2d8ffeb.jpg", spotify_url: "https://open.spotify.com/album/3ZiWyEie02GeviYI6ArLC8" },
  { title: "Ringing", label: "Single", year: "2025", artwork_url: "/api/files/phoon-site/uploads/39094781-6e0c-4231-a952-879d8cb148f2.jpg", spotify_url: "https://open.spotify.com/album/4VgICrQB7Ww36BSbja9xxm" },
  { title: "Move Your Body", label: "Single", year: "2025", artwork_url: "/api/files/phoon-site/uploads/eff6959b-f6ea-4cb8-b943-9d943d39be4e.png", spotify_url: "https://open.spotify.com/album/1XaYIuvvpdeO2CHZ0vhJIA" },
  { title: "Off The Rails", label: "Single", year: "2025", artwork_url: "/api/files/phoon-site/uploads/1271295f-6542-427e-87bd-2c1d6e97caff.jpg", spotify_url: "https://open.spotify.com/album/2lcfQvsN81NXHUvc1mioOa" },
];

function extractSpotifyId(url, type = "track") {
  if (!url) return "";
  const match = url.match(new RegExp(`${type}/([a-zA-Z0-9]+)`));
  if (match) return match[1];
  const albumMatch = url.match(/album\/([a-zA-Z0-9]+)/);
  if (albumMatch) return albumMatch[1];
  const trackMatch = url.match(/track\/([a-zA-Z0-9]+)/);
  if (trackMatch) return trackMatch[1];
  return url;
}

export default function Music() {
  const { content } = useCMS();
  const music = content.music || {};
  const release = music.featured_release || {};
  const discography = (music.discography && music.discography.length > 0) ? music.discography : defaultDiscography;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const trackId = extractSpotifyId(release.spotify_url || release.spotify_track_id, "track");
  const albumId = extractSpotifyId(release.spotify_album_url || release.spotify_album_id, "album");
  const trackUrl = release.spotify_url || `https://open.spotify.com/track/${trackId}`;

  return (
    <main data-testid="music-page" className="pt-20">
      <SEO title="Music" description="Listen to Phöön's latest releases, original tracks, live DJ sets, remixes and edits." path="/music" />
      {/* Page Header */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            data-testid="music-page-title"
            className="font-['Black_Han_Sans'] text-5xl md:text-7xl text-[#F4EFE6] uppercase"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
          MUSIC
          </motion.h1>
        </div>
      </section>

      {/* Featured Release */}
      <section data-testid="featured-release-section" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Featured Release
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.a
              href={trackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative overflow-hidden border border-[#FF5500]/20 block"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            >
              <img
                data-testid="featured-release-artwork"
                src={(() => { if (!release.artwork_url) return ""; return release.artwork_url.startsWith("/") ? `${BACKEND}${release.artwork_url}` : release.artwork_url; })()}
                alt={`${release.title || "Release"} artwork`}
                className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.a>

            <motion.div
              className="flex flex-col justify-center"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} custom={1}
            >
              <h2 data-testid="featured-release-title" className="font-['Black_Han_Sans'] text-4xl md:text-6xl text-[#F4EFE6] uppercase">
                {release.title || "I Was Like"}
              </h2>
              <div className="mt-4 flex items-center gap-4">
                <span className="font-mono-label text-xs text-[#F4EFE6]/60">{release.label || ""}</span>
                <span className="text-[#FF5500]/30">|</span>
                <span className="font-mono-label text-xs text-[#F4EFE6]/60">{release.year || ""}</span>
              </div>

              {trackId && (
                <div className="mt-8" data-testid="featured-spotify-embed">
                  <iframe
                    style={{ borderRadius: "12px" }}
                    src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
                    width="100%"
                    height="152"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={`Phöön — ${release.title || ""}`}
                  />
                </div>
              )}

              {albumId && (
                <a
                  href={release.spotify_album_url || `https://open.spotify.com/album/${albumId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="featured-album-embed"
                  className="mt-3 inline-flex items-center gap-2 font-mono-label text-xs text-[#1DB954]/60 hover:text-[#F4EFE6] transition-colors group"
                >
                  Full Album on Spotify
                  <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Discography Grid */}
      <section data-testid="discography-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Discography
          </motion.p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {discography.map((item, i) => (
              <motion.a
                key={`disc-${item.title}-${item.year}-${i}`}
                href={item.spotify_url || item.link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`release-card-${i}`}
                className="group block"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeInUp}
              >
                <div className="relative overflow-hidden border border-[#FF5500]/10 group-hover:border-[#FF5500]/40 transition-colors duration-300">
                  <img
                    src={(() => { const url = item.artwork_url || item.artwork || ""; return url.startsWith("/") ? `${BACKEND}${url}` : url; })()}
                    alt={item.title}
                    className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="mt-3">
                  <h3 className="font-['Black_Han_Sans'] text-xl md:text-2xl text-[#F4EFE6] group-hover:text-[#FF5500] transition-colors duration-300 uppercase">
                    {item.title}
                  </h3>
                  <p className="font-mono-label text-[10px] text-[#F4EFE6]/50 mt-1">
                    {item.label} &middot; {item.year}
                  </p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Live DJ Sets, Remixes and Edits */}
      <section data-testid="mixes-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Sets + Edits
          </motion.p>

          <div className="space-y-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <div className="bg-[#0c1128] border border-[#FF5500]/20 p-4 overflow-hidden">
                <div className="sc-dark-wrap">
                  <iframe
                    width="100%"
                    height="450"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%253Aplaylists%253A2079801192&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"
                    title="Phöön — Live DJ Sets"
                    data-testid="soundcloud-embed-1"
                  />
                </div>
                <div className="mt-2 font-mono-label text-[10px] text-[#F4EFE6]/50">
                  <a href="https://soundcloud.com/musicbyphoon" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5500] transition-colors">Phöön</a>
                  {" · "}
                  <a href="https://soundcloud.com/musicbyphoon/sets/live-dj-sets" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5500] transition-colors">Live DJ Sets</a>
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} custom={1}>
              <div className="bg-[#0c1128] border border-[#FF5500]/20 p-4 overflow-hidden">
                <div className="sc-dark-wrap">
                  <iframe
                    width="100%"
                    height="450"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%253Aplaylists%253A2079800106&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"
                    title="Phöön — Remixes and Edits"
                    data-testid="soundcloud-embed-2"
                  />
                </div>
                <div className="mt-2 font-mono-label text-[10px] text-[#F4EFE6]/50">
                  <a href="https://soundcloud.com/musicbyphoon" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5500] transition-colors">Phöön</a>
                  {" · "}
                  <a href="https://soundcloud.com/musicbyphoon/sets/remixes-and-edits" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5500] transition-colors">Remixes and Edits</a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
